// Списък нередности — общ екран: наемателят вижда своите (и потвърждава),
// управляващият вижда всички за имотите си (и маркира видяна/отстранена).

import React, { useCallback, useState } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { confirmAction, showError, showSuccess } from '../toast';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, ISSUE_STATUS_LABELS, money } from '../api';
import { showPhoto } from '../photo-viewer';
import { useAuth } from '../AuthContext';
import { regionOfCity } from '../geo';
import { Badge, Button, Card, COLORS, Muted, Screen, Title } from '../ui';
import { getLocale, t } from '../i18n';

const STATUS_COLORS: Record<string, string> = {
  SUBMITTED: '#D97706',
  SEEN: COLORS.primary,
  ASSIGNED: '#7C3AED',
  RESOLVED: COLORS.success,
  CONFIRMED: COLORS.muted,
};

// Чака ли нередността МОЕТО потвърждение?
// Червено значи „ти трябва да направиш нещо“ — не „чака някого“.
// Иначе наемодател, който вече е потвърдил, вижда червено заради наемателя.
// Чака ли нередността МОЕТО потвърждение?
// Червено значи „ти трябва да направиш нещо“ — не „чака някого“.
function awaitsMyConfirmation(issue: any, isManager: boolean, isTenant: boolean): boolean {
  if (issue.status !== 'RESOLVED') return false;
  const hasTenant = !!issue.tenancy?.tenantId;
  const requests = issue.jobRequests ?? [];

  // Работа, по която ВСЕ ОЩЕ се чака потвърждение.
  // Освободен/върнат escrow и приключена заявка НЕ чакат нищо.
  const pending = requests.filter((r: any) => {
    if (r.isDirect) return r.markedDoneAt && r.status !== 'COMPLETED';
    const e = r.offer?.escrowPayment;
    return e?.status === 'HELD' && e.markedDoneAt;
  });

  if (pending.length) {
    return pending.some((r: any) => {
      const [landlordOk, tenantOk] = r.isDirect
        ? [!!r.landlordConfirmedAt, !!r.tenantConfirmedAt]
        : [!!r.offer.escrowPayment.landlordConfirmedAt, !!r.offer.escrowPayment.tenantConfirmedAt];
      if (isManager) return !landlordOk;
      if (isTenant) return hasTenant && !tenantOk;
      return false;
    });
  }

  // Имало е майстор и всичко по него е приключило (платено/върнато) →
  // няма какво да се потвърждава. Иначе наемателят виждаше червено
  // „чака вашето потвърждение“ БЕЗ бутон, по който да действа.
  if (requests.length) return false;

  // Отстранена без майстор → потвърждава наемателят
  return isTenant;
}

export default function IssuesScreen({ navigation }: any) {
  const { user } = useAuth();
  const isManager = user?.role === 'LANDLORD' || user?.role === 'AGENCY';
  const [issues, setIssues] = useState<any[]>([]);
  const [trusted, setTrusted] = useState<any[]>([]);

  const load = useCallback(() => {
    api('/issues').then((r) => setIssues(r.issues)).catch(() => {});
    if (isManager) api('/jobs/trusted').then((r) => setTrusted(r.handymen ?? [])).catch(() => {});
  }, [isManager]);
  useFocusEffect(load);

  // Директна (безплатна) заявка към доверен майстор
  const sendDirect = async (issueId: string, handymanId: string, name: string) => {
    confirmAction(
      t('Доверен майстор'),
      t('Да изпратим ли заявката директно на {name}? Без оферта и без плащане през приложението — разплащането е по ваша лична уговорка.', { name }),
      t('Изпрати'),
      async () => {
        try {
          await api(`/jobs/issues/${issueId}/direct`, { body: { handymanId } });
          showSuccess(t('Заявката е изпратена на доверения майстор.'));
          load();
        } catch (e: any) {
          showError(e.message);
        }
      }
    );
  };

  const act = async (id: string, action: 'seen' | 'resolve' | 'confirm') => {
    try {
      await api(`/issues/${id}/${action}`, { body: {} });
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  return (
    <Screen>
      <Title>{t('Нередности')}</Title>
      {user?.role === 'TENANT' && <Button title={t('+ Подай нередност')} onPress={() => navigation.navigate('NewIssue')} />}
      <View style={{ height: 12 }} />
      {issues.length === 0 && (
        <Card>
          <Muted>{t('Няма нередности.')}</Muted>
        </Card>
      )}
      {issues.map((issue) => {
        const mine = awaitsMyConfirmation(issue, isManager, user?.role === 'TENANT');
        return (
        <Card key={issue.id} style={mine ? { borderColor: COLORS.danger, borderWidth: 2 } : {}}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontWeight: '600', fontSize: 16, color: COLORS.text, flex: 1 }}>
              {issue.title || issue.description.slice(0, 40)}
            </Text>
            {mine ? (
              // Чака МОЕТО потвърждение → червен кръг, за да се набива на очи
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <View
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    backgroundColor: COLORS.danger,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>1</Text>
                </View>
                <Badge text={t('Чака вашето потвърждение')} color={COLORS.danger} />
              </View>
            ) : issue.status === 'RESOLVED' ? (
              // Отстранена, но чака ДРУГАТА страна — спокоен цвят
              <Badge text={isManager ? t('Чака наемателя') : t('Чака наемодателя')} color="#D97706" />
            ) : (
              <Badge text={t(ISSUE_STATUS_LABELS[issue.status] ?? issue.status)} color={STATUS_COLORS[issue.status]} />
            )}
          </View>
          <Muted>
            {issue.apartment.address} · {new Date(issue.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}
            {isManager ? ` · ${t('от {name}', { name: `${issue.reportedBy.firstName} ${issue.reportedBy.lastName}` })}` : ''}
          </Muted>
          <Text style={{ marginTop: 6, color: COLORS.text }}>{issue.description}</Text>
          {issue.photos.length > 0 && (
            <ScrollView horizontal style={{ marginTop: 8 }}>
              {issue.photos.map((p: string) => (
                <Pressable key={p} onPress={() => showPhoto(`${API_URL}${p}`)}>
                  <Image source={{ uri: `${API_URL}${p}` }} style={{ width: 90, height: 90, borderRadius: 8, marginRight: 8 }} />
                </Pressable>
              ))}
            </ScrollView>
          )}

          {isManager && issue.status === 'SUBMITTED' && (
            <Button title={t('Маркирай като видяна')} variant="secondary" onPress={() => act(issue.id, 'seen')} />
          )}
          {isManager && (issue.status === 'SUBMITTED' || issue.status === 'SEEN') && (
            <>
              {/* Доверен майстор → директно и безплатно. Търсене → стандартният платен поток. */}
              {trusted.map((h) => (
                <Button
                  key={h.id}
                  title={t('⭐ Прати на {name} (безплатно)', { name: `${h.firstName} ${h.lastName}` })}
                  onPress={() => sendDirect(issue.id, h.id, `${h.firstName} ${h.lastName}`)}
                />
              ))}
              <Button
                title={trusted.length ? t('Потърси друг майстор →') : t('Потърси майстор →')}
                variant={trusted.length ? 'secondary' : 'primary'}
                onPress={() =>
                  navigation.navigate('HandymenPick', {
                    issueId: issue.id,
                    region: issue.apartment?.region ?? regionOfCity(issue.apartment?.city),
                  })
                }
              />
            </>
          )}

          <JobSection issue={issue} isManager={isManager} isTenant={user?.role === 'TENANT'} reload={load} navigation={navigation} />

          {isManager && issue.status === 'SEEN' && !issue.jobRequests?.length && (
            <Button title={t('Отстраних я сам')} variant="secondary" onPress={() => act(issue.id, 'resolve')} />
          )}
          {!isManager &&
            issue.status === 'RESOLVED' &&
            !issue.jobRequests?.some((r: any) => r.offer?.escrowPayment) && (
              <Button title={t('Потвърждавам — отстранена е')} onPress={() => act(issue.id, 'confirm')} />
            )}
        </Card>
        );
      })}
    </Screen>
  );
}

// Оферти от майстори + escrow действия (потвърждение/спор) по нередност
function JobSection({ issue, isManager, isTenant, reload, navigation }: any) {
  const jobAct = async (path: string, body: object = {}) => {
    try {
      await api(path, { body });
      reload();
    } catch (e: any) {
      showError(e.message);
    }
  };

  const requests = issue.jobRequests ?? [];
  // Без наемател (напр. ремонт в празен апартамент) → потвърждава само наемодателят.
  const hasTenant = !!issue.tenancy?.tenantId;
  if (requests.length === 0) return null;

  return (
    <View style={{ marginTop: 8 }}>
      {requests.map((r: any) => {
        const escrow = r.offer?.escrowPayment;
        return (
          <View key={r.id} style={{ paddingVertical: 6, borderTopWidth: 1, borderTopColor: COLORS.border }}>
            <Text style={{ color: COLORS.text, fontWeight: '500' }}>
              🔧 {r.handyman.firstName} {r.handyman.lastName}
              {r.handyman.handymanProfile?.ratingAvg ? ` · ★ ${Number(r.handyman.handymanProfile.ratingAvg).toFixed(1)}` : ''}
            </Text>

            {/* --- ДИРЕКТНА заявка към доверен майстор: без пари, но със задължително двойно потвърждение --- */}
            {r.isDirect ? (
              <>
                <Badge text={t('⭐ Доверен майстор — без плащане през приложението')} color={COLORS.success} />
                {r.status === 'SENT' && <Muted>{t('Чака майсторът да приеме…')}</Muted>}
                {r.status === 'ACCEPTED' && !r.markedDoneAt && <Muted>{t('Прие заявката — предстои ремонтът.')}</Muted>}
                {r.completionPhotoUrl && (
                  <Pressable onPress={() => showPhoto(`${API_URL}${r.completionPhotoUrl}`)}>
                    <Image
                      source={{ uri: `${API_URL}${r.completionPhotoUrl}` }}
                      style={{ width: 110, height: 110, borderRadius: 8, marginVertical: 6 }}
                    />
                  </Pressable>
                )}
                {r.markedDoneAt && r.status !== 'COMPLETED' && (
                  <>
                    <Badge
                      text={
                        hasTenant
                          ? t('Потвърждения: наемодател {a} · наемател {b}', { a: r.landlordConfirmedAt ? '✓' : '—', b: r.tenantConfirmedAt ? '✓' : '—' })
                          : t('Потвърждение: наемодател {a} (без наемател)', { a: r.landlordConfirmedAt ? '✓' : '—' })
                      }
                      color={COLORS.primary}
                    />
                    {((isManager && !r.landlordConfirmedAt) || (hasTenant && isTenant && !r.tenantConfirmedAt)) && (
                      <Button title={t('Потвърждавам ремонта ✓')} onPress={() => jobAct(`/jobs/requests/${r.id}/direct-confirm`)} />
                    )}
                  </>
                )}
                {r.status === 'COMPLETED' && (
                  <>
                    <Badge text={t('Потвърдено от двете страни ✓')} color={COLORS.success} />
                    {isManager && (
                      <Button
                        title={t('Оцени майстора ★')}
                        variant="secondary"
                        onPress={() =>
                          navigation.navigate('Review', {
                            jobRequestId: r.id,
                            targetName: `${r.handyman.firstName} ${r.handyman.lastName}`,
                          })
                        }
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              !r.offer && <Muted>{r.status === 'SENT' ? t('Чака оферта…') : t('Затворено')}</Muted>
            )}

            {r.offer && !escrow && r.offer.status === 'PENDING' && (
              <>
                <Muted>{t('Оферта: {price} · срок {days} дни', { price: money(r.offer.priceCents), days: r.offer.daysToComplete })}{r.offer.message ? ` · „${r.offer.message}“` : ''}</Muted>
                {isManager && (
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <View style={{ flex: 1 }}>
                      <Button
                        title={t('Приеми офертата')}
                        onPress={() =>
                          navigation.navigate('HoldPayment', {
                            offerId: r.offer.id,
                            amountCents: r.offer.priceCents,
                            handyman: `${r.handyman.firstName} ${r.handyman.lastName}`,
                            description: issue.description,
                          })
                        }
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Button title={t('Откажи')} variant="danger" onPress={() => jobAct(`/jobs/offers/${r.offer.id}/reject`)} />
                    </View>
                  </View>
                )}
              </>
            )}

            {escrow && (
              <>
                <Muted>{t('Задържани: {amount}', { amount: money(escrow.amountCents) })}</Muted>
                {escrow.completionPhotoUrl && (
                  <Pressable onPress={() => showPhoto(`${API_URL}${escrow.completionPhotoUrl}`)}>
                    <Image
                      source={{ uri: `${API_URL}${escrow.completionPhotoUrl}` }}
                      style={{ width: 110, height: 110, borderRadius: 8, marginVertical: 6 }}
                    />
                  </Pressable>
                )}
                {escrow.status === 'HELD' && !escrow.markedDoneAt && <Badge text={t('Чака изпълнение от майстора')} color="#D97706" />}
                {escrow.status === 'HELD' && escrow.markedDoneAt && (
                  <>
                    <Badge
                      text={
                        hasTenant
                          ? t('Потвърждения: наемодател {a} · наемател {b}', { a: escrow.landlordConfirmedAt ? '✓' : '—', b: escrow.tenantConfirmedAt ? '✓' : '—' })
                          : t('Потвърждение: наемодател {a} (без наемател)', { a: escrow.landlordConfirmedAt ? '✓' : '—' })
                      }
                      color={COLORS.primary}
                    />
                    {((isManager && !escrow.landlordConfirmedAt) || (hasTenant && isTenant && !escrow.tenantConfirmedAt)) && (
                      <Button title={t('Потвърждавам ремонта ✓')} onPress={() => jobAct(`/jobs/escrow/${escrow.id}/confirm`)} />
                    )}
                    {(isManager || isTenant) && (
                      <Button
                        title={t('Проблем с ремонта (спор)')}
                        variant="danger"
                        onPress={() => jobAct(`/jobs/escrow/${escrow.id}/dispute`, { reason: 'Ремонтът не е приет' })}
                      />
                    )}
                  </>
                )}
                {escrow.status === 'RELEASED' && (
                  <>
                    <Badge text={t('Платено на майстора ✓')} color={COLORS.success} />
                    {isManager && (
                      <Button
                        title={t('Оцени майстора ★')}
                        variant="secondary"
                        onPress={() =>
                          navigation.navigate('Review', {
                            offerId: r.offer.id,
                            targetName: `${r.handyman.firstName} ${r.handyman.lastName}`,
                          })
                        }
                      />
                    )}
                  </>
                )}
                {escrow.status === 'DISPUTED' && <Badge text={t('В спор — при модератор')} color={COLORS.danger} />}
                {escrow.status === 'REFUNDED' && <Badge text={t('Парите са върнати')} color={COLORS.danger} />}
              </>
            )}
          </View>
        );
      })}
    </View>
  );
}
